import { useCallback } from 'react';
import { useUser } from '../../hooks/useUser';
import { getUsersApiError, usersApiClient } from '../../clients/users-api';
import {
  CreateUserAddressRequest,
  CustomerDto,
  UpdateUserAddressRequest,
  UserAddressDto,
} from '@kotipizzagroup/kotipizza-users-api-client';
import { trackInsightsException } from '../../utils/telemetry/appinsights';
import { newAbortSignal } from '../../utils/axiosUtils';

export const useAddressMutationFunctions = (): {
  updateAddress: (address: UpdateUserAddressRequest) => Promise<void>;
  createAddress: (address: CreateUserAddressRequest) => Promise<UserAddressDto>;
  deleteAddress: (address: UserAddressDto) => Promise<void>;
  setDefaultAddress: (address: UserAddressDto) => Promise<void>;
} => {
  const { user, refetchUserData } = useUser();

  const updateAddress = useCallback(
    (address: UpdateUserAddressRequest) => {
      return updateAddressImplementation(user, refetchUserData, address);
    },
    [user, refetchUserData]
  );

  const setDefaultAddress = useCallback(
    (address: UserAddressDto) => {
      return setDefaultAddressImplementation(user, refetchUserData, address);
    },
    [user, refetchUserData]
  );

  const createAddress = useCallback(
    (address: CreateUserAddressRequest) => {
      return createAddressImplementation(address, refetchUserData);
    },
    [refetchUserData]
  );

  const deleteAddress = useCallback(
    (address: UserAddressDto) => {
      return deleteAddressImplementation(address, refetchUserData);
    },
    [refetchUserData]
  );

  return {
    createAddress,
    setDefaultAddress,
    updateAddress,
    deleteAddress,
  };
};

const updateAddressImplementation = async (
  user: CustomerDto | null,
  refetchUserData: () => Promise<CustomerDto | null>,
  address: UpdateUserAddressRequest
): Promise<void> => {
  if (!user) {
    return;
  }

  try {
    await usersApiClient.updateUserAddress(address.addressId, address, newAbortSignal());
    await refetchUserData();
  } catch (error) {
    trackInsightsException('FRONTEND_ADDRESS_UPDATE_ERROR', error, {
      addressId: address.addressId,
    });
    throw getUsersApiError(error);
  }
};

const setDefaultAddressImplementation = async (
  user: CustomerDto | null,
  refetchUserData: () => Promise<CustomerDto | null>,
  address: UserAddressDto
): Promise<void> => {
  if (!user) {
    return;
  }

  try {
    await usersApiClient.setUserPrimaryAddress(address.addressId, newAbortSignal());
    await refetchUserData();
  } catch (error) {
    trackInsightsException('FRONTEND_ADDRESS_PRIMARY_ERROR', error, {
      addressId: address.addressId,
    });
    throw getUsersApiError(error);
  }
};

const createAddressImplementation = async (
  address: CreateUserAddressRequest,
  refetchUserData: () => Promise<CustomerDto | null>
): Promise<UserAddressDto> => {
  try {
    const newAddress = await usersApiClient.createUserAddress(address, newAbortSignal());
    await refetchUserData();
    return newAddress;
  } catch (error) {
    trackInsightsException('FRONTEND_ADDRESS_CREATE_ERROR', error, {
      address: address,
    });
    throw getUsersApiError(error);
  }
};

const deleteAddressImplementation = async (
  address: UserAddressDto,
  refetchUserData: () => Promise<CustomerDto | null>
): Promise<void> => {
  try {
    await usersApiClient.removeUserAddress(address.addressId, newAbortSignal());
    await refetchUserData();
  } catch (error) {
    trackInsightsException('FRONTEND_ADDRESS_DELETE_ERROR', error, {
      addressId: address.addressId,
    });
    throw getUsersApiError(error);
  }
};
